// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice } from '@reduxjs/toolkit';
import type { ChatMessage } from './chatbotTypes';
import {
  SendChatTurnStartAction, SendChatTurnSuccessAction, SendChatTurnFailureAction, SendChatTurnDoneAction,
  SetChatProgressAction, RestoreSessionAction, ResetSessionAction, SetSessionStaleAction, ClearChatAction,
} from './chatbotActions';

// The Khora conversation state. The transcript is NEVER persisted client-side (PII-adjacent); it lives
// in Redux only for the session, and is saved server-side / rehydrated per gdb version by chatbotThunk.
interface IChatbotState {
  messages: ChatMessage[];
  loading: boolean;
  // Live per-turn tool status polled from the server; null → the ThinkingIndicator's timed fallback.
  progress: string | null;
  error: string | null;
  // True when the gdb was updated since this session was saved — the UI shows a dismissable chip.
  sessionStale: boolean;
}

const initialState: IChatbotState = {
  messages: [],
  loading: false,
  progress: null,
  error: null,
  sessionStale: false,
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(SendChatTurnStartAction, (state, action) => {
        state.messages = action.payload; // optimistic: prior transcript + the user's new message
        state.loading = true;
        state.error = null;
        state.progress = null;
      })
      .addCase(SendChatTurnSuccessAction, (state, action) => {
        state.messages = action.payload; // transcript with Khora's reply appended
      })
      .addCase(SendChatTurnFailureAction, (state, action) => {
        state.error = action.payload; // the user's message stays visible above the error
      })
      .addCase(SendChatTurnDoneAction, (state) => {
        state.loading = false;
        state.progress = null;
      })
      .addCase(SetChatProgressAction, (state, action) => {
        state.progress = action.payload;
      })
      .addCase(RestoreSessionAction, (state, action) => {
        state.messages = action.payload.messages;
        state.sessionStale = action.payload.stale;
      })
      .addCase(ResetSessionAction, (state) => {
        state.messages = [];
        state.sessionStale = false;
      })
      .addCase(SetSessionStaleAction, (state, action) => {
        state.sessionStale = action.payload;
      })
      .addCase(ClearChatAction, (state) => {
        state.messages = [];
        state.error = null;
      });
  },
});

export const { reducer } = chatbotSlice;
export default reducer;
